import { useEffect } from 'react';
import { useFormState } from 'react-use-form-state';
import { useToasts } from 'react-toast-notifications';
import { useMutation, useQuery } from '@apollo/react-hooks';

import { UPDATE_NEXT_OF_KIN, CREATE_NEXT_OF_KIN, NEXT_OF_KIN_QUERY } from './queries';

const _v = {
  firstName: 'firstName',
  lastName: 'lastName',
  phone: 'phone',
  email: 'email',
  address: 'address',
  city: 'city',
  stateOfResidence: 'stateOfResidence',
  gender: 'gender',
  maritalStatus: 'maritalStatus',
  dateOfBirth: 'dateOfBirth',
};

const useUpdateNextOfKin = ({ customer } = {}) => {
  if (!customer) throw new Error('No customer supplied to provider');
  const { data: { viewer: { nextOfKin } = {} } = {}, loading: qLoading } = useQuery(NEXT_OF_KIN_QUERY);

  const [formState, form] = useFormState();

  useEffect(() => {
    if (nextOfKin && formState.isPristine()) {
      formState.setField([_v.firstName], nextOfKin.firstName);
      formState.setField([_v.lastName], nextOfKin.lastName);
      formState.setField([_v.phone], nextOfKin.phone);
      formState.setField([_v.email], nextOfKin.email);
      formState.setField([_v.address], nextOfKin.address);
      formState.setField([_v.city], nextOfKin.city);
      formState.setField([_v.stateOfResidence], nextOfKin.stateOfResidence);
      formState.setField([_v.gender], nextOfKin.gender);
      formState.setField([_v.maritalStatus], nextOfKin.maritalStatus);
      formState.setField([_v.dateOfBirth], nextOfKin.dateOfBirth);
    }
  }, [nextOfKin, formState]);
  const { addToast } = useToasts();

  const [updateNextOfKin, { loading: uLoading }] = useMutation(UPDATE_NEXT_OF_KIN, {
    onCompleted: () => {
      addToast('Your next of kin details have been updated', {
        appearance: 'success',
        autoDismiss: true,
        autoDismissTimeout: 8000,
      });
    },
    onError: (err) => {
      console.log(err);
      if (err.graphQLErrors) {
        err.graphQLErrors.forEach((e) => {
          if (
            e.message.includes('E11000 duplicate key error collection: browncon.nextOfKins index: email_1 dup key:')
          ) {
            addToast(
              "Seems you are already registered with us. Can't wait to work with you, We'll get back to you shortly",
              {
                appearance: 'success',
                autoDismiss: true,
                autoDismissTimeout: 10000,
              },
            );
          } else {
            addToast(e.message, {
              appearance: 'error',
              autoDismiss: true,
            });
          }
        });
      } else {
        addToast('Something unexpected happened', {
          appearance: 'error',
          autoDismiss: true,
        });
      }
    },
  });

  const [createNextOfKin, { loading: cLoading }] = useMutation(CREATE_NEXT_OF_KIN, {
    onCompleted: () => {
      addToast('Your next of kin details have been created', {
        appearance: 'success',
        autoDismiss: true,
        autoDismissTimeout: 8000,
      });
    },
    onError: (err) => {
      console.log(err);
      if (err.graphQLErrors) {
        err.graphQLErrors.forEach((e) => {
          if (
            e.message.includes('E11000 duplicate key error collection: browncon.nextOfKins index: email_1 dup key:')
          ) {
            addToast(
              "Seems you are already registered with us. Can't wait to work with you, We'll get back to you shortly",
              {
                appearance: 'success',
                autoDismiss: true,
                autoDismissTimeout: 10000,
              },
            );
          }
        });
      } else {
        addToast('Something unexpected happened', {
          appearance: 'error',
          autoDismiss: true,
        });
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    // TODO: validation
    if (nextOfKin) {
      updateNextOfKin({ variables: { nextOfKinId: nextOfKin.id, data: formState.values } });
    } else {
      createNextOfKin({ variables: { customerId: customer.id, data: formState.values } });
    }
  };

  return {
    _v,
    form,
    formState,
    handleSubmit,
    nextOfKin,
    loading: qLoading || uLoading || cLoading,
  };
};

export default useUpdateNextOfKin;
